import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/Button';
import fieldSelection from 'components/Forms/Fields';
import styles from './index.module.scss';
import DetailMediaUploader from './DetailMediaUploader';
import { formLabels } from './options';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';

const useStyles = makeStyles(theme => ({
  formContainerBackground: {
    background: theme.palette.background.default,
  },
}));

const DetailedMediaSubForm = ({
  fields,
  defaultValues,
  submit,
  exception,
  cancel,
  requiredFields,
  editValues,
  label,
  mediaType,
  formProps,
  noOfMediaFilesAllowed,
}) => {
  const [values, setValues] = useState(defaultValues);
  const [touched, setTouched] = useState({});
  const classes = useStyles();
  const [errors, setErrors] = useState({});

  const setFieldTouched = key => {
    setTouched({ ...touched, [key]: true });
  };
  useEffect(() => {
    // If there are edit values then override state variable 'values' with 'editValues'
    if (
      editValues.length > 0 &&
      values.surfaces === '' &&
      values?.surfaces[0]?.name !== editValues[0].name
    ) {
      setValues(prevVal => ({
        ...prevVal,
        surfaces: editValues,
        name: editValues[0].name,
        description: editValues[0].description,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.surfaces, editValues]);

  const handleChange = event => {
    const key = event.target.name;
    const { value } = event.target;
    if (key === exception) {
      setValues({
        ...values,
        ...value,
        [key]: value,
      });
    } else {
      setValues({
        ...values,
        [key]: value,
      });
    }
  };

  const handleImageChange = image => {
    if (editValues.length > 0) {
      // if image is changed while editing, it should perserve the index
      // and only change the new image url
      const newArray = editValues.map(val => ({
        ...val,
        url: image[0].url,
        mediaType: image[0].mediaType,
      }));

      setValues({
        ...values,
        surfaces: newArray,
        name: image[0].name.replace(/\.[^/.]+$/, ''),
      });
    } else {
      setValues({
        ...values,
        surfaces: image,
        height: image.height,
        width: image.width,
        name:
          values.name !== '' && values.name.trim()
            ? values.name
            : image[0].name.replace(/\.[^/.]+$/, ''),
        id: uuidv4(),
      });
    }
    // if images are added or edited successfully then there should be no errors
    setTouched({});
    setErrors({});
  };
  const setFieldValue = (key, value) => {
    setValues({ ...values, [key]: value });
  };

  const onAddItem = () => {
    let tempErrors = {};
    let tempTouched = {};
    // check for empty values
    requiredFields.forEach(keyName => {
      if (values[keyName] === '') {
        tempErrors = { ...tempErrors, [keyName]: 'required_label' };
        tempTouched = { ...tempTouched, [keyName]: true };
      }
    });
    setTouched(tempTouched);
    setErrors(tempErrors);
    if (
      Object.keys(tempErrors).length === 0 &&
      Object.keys(tempTouched).length === 0
    ) {
      let surfaceWithNameAndDesc = [...values.surfaces];

      //copy description and name in the surfaces key incase they are set
      if (values.description !== '') {
        surfaceWithNameAndDesc = surfaceWithNameAndDesc.map(m => ({
          ...m,
          description: values.description,
        }));
      }

      if (values.name !== '') {
        surfaceWithNameAndDesc = surfaceWithNameAndDesc.map(m => ({
          ...m,
          name: values.name,
        }));
      }

      // add id to surface
      if (values.id !== '') {
        surfaceWithNameAndDesc = surfaceWithNameAndDesc.map(m => ({
          ...m,
          id: values.id ? values.id : uuidv4(),
        }));
      }
      // remove extension from the name
      const surfacesWithoutExt = surfaceWithNameAndDesc.map(image => ({
        ...image,
        name: image.name.replace(/\.[^/.]+$/, ''),
      }));

      submit(surfacesWithoutExt);
    }
  };

  const props = {
    values,
    touched,
    errors,
    setFieldTouched,
    handleChange,
    setFieldValue,
  };

  return (
    <div className={cn(styles.formContainer, classes.formContainerBackground)}>
      <form className={styles.form}>
        <div className={styles.fixedLeft}>
          <DetailMediaUploader
            onChange={handleImageChange}
            error={errors['surfaces']}
            displaySelectedMedia={
              values?.surfaces[0]?.url
                ? values?.surfaces[0]?.url
                : values?.surfaces[0]?.imageUrl
            }
            label={label}
            mediaType={mediaType}
            formProps={formProps}
            noOfMediaFilesAllowed={noOfMediaFilesAllowed}
          />
        </div>
        <div className={styles.inputs}>
          {fields.map((field, index) =>
            fieldSelection({ item: field, key: index, id: index, props }),
          )}
          <Button
            type="button"
            text={formLabels.buttonCancel}
            onClick={cancel}
            size="l"
            color="secondary"
            outline={true}
          />
          <Button
            type="button"
            onClick={onAddItem}
            text={formLabels.buttonSubmit}
            size="l"
            color="primary"
          />
        </div>
      </form>
    </div>
  );
};

DetailedMediaSubForm.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object),
  defaultValues: PropTypes.object.isRequired,
  submit: PropTypes.func.isRequired,
  exception: PropTypes.string,
  cancel: PropTypes.func.isRequired,
  requiredFields: PropTypes.array,
  value: PropTypes.array,
  editValues: PropTypes.array,
  label: PropTypes.string,
  mediaType: PropTypes.array,
  formProps: PropTypes.object,
  noOfMediaFilesAllowed: PropTypes.number,
};

export default DetailedMediaSubForm;
